import { type ComputedRef, reactive, type Ref, ref, watchEffect } from 'vue'

import { normalizeNotificationSubscription } from '@/lib/adapters/notificationSubscription.adapters'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { notificationRequest } from '@/lib/services'
import { RequestStatus } from '@/lib/types/common.types'
import type {
  NotificationSubscriptionPayloadType,
  NotificationSubscriptionType,
} from '@/lib/types/notificationSubscription.types'
import { useErrorStore } from '@/stores/error.stores'
import { useUserStore } from '@/stores/user.stores'

export const useNotificationSubscriptionInfo = (
  subscriberId: ComputedRef<string>,
  campaignId: string,
) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const status = ref<RequestStatus>(RequestStatus.Idle)
  const data = ref<NotificationSubscriptionType | null>(null)

  const getNotificationSubscriptions = async (subscriberId: string, campaignId: string) => {
    try {
      status.value = RequestStatus.Loading
      const topic = `campaign:${campaignId}`
      const response = await notificationRequest.get(
        `/subscribers/${subscriberId}/subscription/${topic}`,
      )

      data.value = normalizeNotificationSubscription(response.data)
      status.value = RequestStatus.Success
    } catch (e: unknown) {
      status.value = RequestStatus.Error
      error.value = e as Error
    }
  }

  const updateSubscription = async (
    subscriberId: string,
    data: NotificationSubscriptionPayloadType,
  ) => {
    try {
      await notificationRequest.put(`/subscribers/${subscriberId}/subscription`, data)
      status.value = RequestStatus.Success
    } catch (e: unknown) {
      status.value = RequestStatus.Error
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    }
  }

  watchEffect(() => {
    if (subscriberId.value && campaignId) {
      void getNotificationSubscriptions(subscriberId.value, campaignId)
    }
  })

  return {
    data,
    status,
    updateSubscription,
  }
}

export const updateNotificationSubscriptionChannels = () => {
  const errorStore = useErrorStore()
  const status = ref<RequestStatus>(RequestStatus.Idle)

  const updateSubscriptionChannels = async (
    subscriberId: number,
    data: {
      inApp: boolean
      email: boolean
    },
  ) => {
    try {
      status.value = RequestStatus.Loading
      await notificationRequest.put(`/subscribers/${subscriberId}/subscription/channels`, {
        in_app: data.inApp,
        email: data.email,
      })
      status.value = RequestStatus.Success
    } catch (e: unknown) {
      status.value = RequestStatus.Error
      errorStore.setError(handleErrorMessage(e))
    }
  }

  return { status, updateSubscriptionChannels }
}

export const updateNotificationSubscriptionReadStatus = async (
  subscriberId: string,
  subject: string,
  status: 'read' | 'unread',
) => {
  await notificationRequest.put(
    `/subscribers/${subscriberId}/subject-changes/${subject}/read-status`,
    { status },
  )
}

export const useGetNotificationStatuses = (subjects: Ref<string[]>) => {
  const subscriptionsMap = reactive(new Map<string, boolean>())
  const userStore = useUserStore()
  const isLoading = ref(true)
  const error = ref<Error | null>(null)

  const fetchStatuses = async (limit = 20) => {
    try {
      isLoading.value = true
      error.value = null

      const { data } = await notificationRequest.post(
        `/subscribers/${userStore.subscriberId}/subject-changes/find-unread`,
        {
          subjects: subjects.value,
          limit,
        },
      )
      data.subjects.forEach((item: { subject: string }) => subscriptionsMap.set(item.subject, true))
    } catch (e) {
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const hasUpdates = (subject: string) => {
    return subscriptionsMap.has(subject)
  }

  watchEffect(() => {
    if (subjects.value.length > 0 && userStore.subscriberId) {
      void fetchStatuses()
    }
  })

  return {
    hasUpdates,
    fetchStatuses,
  }
}
